// src/pages/admin/AdminDashboard.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import AdminLayout from "./AdminLayout";
import { fetchAdminDashboardStats } from "../../lib/adminAuth";

// ✅ NEW
import { listAdminGalleryFolders } from "../../lib/adminApi";

const StatCard = ({ label, value }) => (
  <Card className="rounded-2xl border-stone-200 shadow-sm">
    <CardContent className="p-5">
      <p className="text-sm text-stone-500">{label}</p>
      <h3 className="text-2xl font-bold text-stone-900 mt-1">{value ?? 0}</h3>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [galleryFolders, setGalleryFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  const galleryCounts = useMemo(() => {
    const folders = galleryFolders || [];
    const images = folders.reduce((acc, f) => acc + Number(f.images_count || 0), 0);
    const videos = folders.reduce((acc, f) => acc + Number(f.videos_count || 0), 0);
    return { folders: folders.length, images, videos };
  }, [galleryFolders]);

  const load = async () => {
    try {
      const [data, gallery] = await Promise.all([
        fetchAdminDashboardStats(),
        listAdminGalleryFolders().catch(() => ({ items: [] })),
      ]);

      setStats(data);
      setGalleryFolders(gallery?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Admin Dashboard">
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-stone-200 animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Admin Dashboard" onRefresh={load}>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Projects" value={stats?.projects ?? 0} />
        <StatCard label="Events" value={stats?.events ?? 0} />
        <StatCard label="Blog Posts" value={stats?.blog_posts ?? 0} />
        <StatCard label="Pending Comments" value={stats?.pending_comments ?? 0} />
        <StatCard label="Contacts" value={stats?.contacts ?? 0} />
        <StatCard label="Volunteers" value={stats?.volunteers ?? 0} />
        <StatCard label="Partners" value={stats?.partners ?? 0} />
        <StatCard label="Newsletter" value={stats?.newsletter_subscriptions ?? 0} />

        {/* ✅ NEW: Gallery summary */}
        <StatCard label="Gallery Folders" value={galleryCounts.folders} />
        <StatCard label="Gallery Photos" value={galleryCounts.images} />
        <StatCard label="Gallery Videos" value={galleryCounts.videos} />
      </div>

      <div className="mt-8 grid xl:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-stone-200 xl:col-span-2">
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-900 mb-3">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-amber-700 hover:bg-amber-800 rounded-xl">
                <Link to="/admin/events">Manage Events</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl">
                <Link to="/admin/events/new">Create New Event</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl">
                <Link to="/admin/projects">Manage Projects</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl">
                <Link to="/admin/posts">Manage Blog Posts</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl">
                <Link to="/admin/posts/new">Create New Post</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl">
                <Link to="/admin/comments">Approve Comments</Link>
              </Button>

              {/* ✅ NEW */}
              <Button asChild variant="outline" className="rounded-xl">
                <Link to="/admin/gallery">Manage Gallery</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-stone-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-900 mb-2">Impact Snapshot</h3>
            <p className="text-stone-600">
              Lives impacted: <b>{stats?.lives_impacted ?? 0}</b>
            </p>
            <p className="text-stone-600">
              Communities reached: <b>{stats?.communities_reached ?? 0}</b>
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;